import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { PLAN_DEFINITIONS, getParticipantLimitLabel } from "../../config/plans";
import { fetchMySubscription } from "../../services/api/subscriptions";
import { getMyLeagues } from "../../services/api/leagues";

const PLAN_ACCENTS = {
  launch: "#4caf50",
  "pickup-pro": "#1976d2",
  league: "#7c3aed",
};

const STATUS_LABELS = {
  active: { label: "Active", color: "success" },
  trialing: { label: "Trial", color: "info" },
  past_due: { label: "Past Due", color: "warning" },
  canceled: { label: "Canceled", color: "error" },
  incomplete: { label: "Incomplete", color: "warning" },
};

export default function MySubscriptionView() {
  const navigate = useNavigate();
  const [subscription, setSubscription] = React.useState(null);
  const [participantCount, setParticipantCount] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    Promise.allSettled([fetchMySubscription(), getMyLeagues()])
      .then(([subResult, leaguesResult]) => {
        if (subResult.status === "fulfilled") {
          setSubscription(subResult.value);
        } else {
          setError("Unable to load subscription details.");
        }
        if (leaguesResult.status === "fulfilled" && Array.isArray(leaguesResult.value)) {
          const total = leaguesResult.value.reduce(
            (sum, l) => sum + (Array.isArray(l.participants) ? l.participants.length : 0),
            0
          );
          setParticipantCount(total);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <LinearProgress />
      </Container>
    );
  }

  if (error || !subscription) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || "No active subscription found."}
        </Alert>
        <Button variant="contained" onClick={() => navigate("/pricing")}>
          View Plans
        </Button>
      </Container>
    );
  }

  const plan = PLAN_DEFINITIONS.find((p) => p.id === subscription.planId) ?? PLAN_DEFINITIONS[0];
  const accentColor = PLAN_ACCENTS[plan.id] ?? "#4caf50";
  const statusMeta = STATUS_LABELS[subscription.status] ?? { label: subscription.status, color: "default" };
  const planIndex = PLAN_DEFINITIONS.findIndex((p) => p.id === plan.id);
  const upgradePlans = PLAN_DEFINITIONS.slice(planIndex + 1);
  const usagePercent =
    participantCount != null && plan.participantLimit > 0
      ? Math.min(100, Math.round((participantCount / plan.participantLimit) * 100))
      : null;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h3" fontWeight={700}>
            My Subscription
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage your Puck-Pickup plan and billing.
          </Typography>
        </Box>

        <Card
          variant="outlined"
          sx={{ borderTop: `4px solid ${accentColor}` }}
        >
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="h5" fontWeight={700}>
                    {plan.name}
                  </Typography>
                  <Chip
                    label={statusMeta.label}
                    color={statusMeta.color}
                    size="small"
                  />
                </Stack>
                <Chip
                  size="small"
                  variant="outlined"
                  label={getParticipantLimitLabel(plan.participantLimit)}
                  sx={{ borderColor: accentColor, color: accentColor }}
                />
              </Stack>

              {plan.positioning && (
                <Typography variant="body2" color="text.secondary">
                  {plan.positioning}
                </Typography>
              )}

              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography variant="body1" fontWeight={600}>
                  {plan.monthlyPrice === 0
                    ? "Free"
                    : subscription.billingCycle === "annual"
                    ? `${plan.annualPriceLabel} billed annually`
                    : `$${plan.monthlyPrice}/month`}
                </Typography>
                {plan.monthlyPrice > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {subscription.billingCycle === "annual" ? "Annual billing" : "Monthly billing"}
                  </Typography>
                )}
              </Stack>

              {usagePercent != null && (
                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Participant usage
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {participantCount} / {plan.participantLimit}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={usagePercent}
                    color={usagePercent >= 90 ? "warning" : "primary"}
                    sx={{ borderRadius: 4, height: 8 }}
                  />
                  {usagePercent >= 80 && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: "block" }}>
                      {usagePercent >= 90
                        ? "You're near your participant limit. Consider upgrading."
                        : "Approaching your participant limit."}
                    </Typography>
                  )}
                </Box>
              )}

              <Divider />

              <Typography variant="subtitle2" color="text.secondary">
                Plan features
              </Typography>
              <Stack spacing={0.75}>
                {plan.features.map((feature) => (
                  <Stack key={feature} direction="row" spacing={1} alignItems="flex-start">
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 16, color: accentColor, mt: "2px", flexShrink: 0 }}
                    />
                    <Typography variant="body2">{feature}</Typography>
                  </Stack>
                ))}
              </Stack>

              {plan.monthlyPrice > 0 && (
                <>
                  <Divider />
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={() => navigate("/pricing")}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Manage billing on Pricing page
                  </Button>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>

        {upgradePlans.length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upgrade your plan
            </Typography>
            <Stack spacing={2}>
              {upgradePlans.map((upgradePlan) => {
                const upgradeAccent = PLAN_ACCENTS[upgradePlan.id] ?? "#1976d2";
                return (
                  <Card
                    key={upgradePlan.id}
                    variant="outlined"
                    sx={{
                      borderLeft: `4px solid ${upgradeAccent}`,
                      bgcolor: (theme) =>
                        alpha(upgradeAccent, theme.palette.mode === "dark" ? 0.08 : 0.04),
                    }}
                  >
                    <CardContent>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                        spacing={2}
                      >
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle1" fontWeight={700}>
                              {upgradePlan.name}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={getParticipantLimitLabel(upgradePlan.participantLimit)}
                              sx={{ borderColor: upgradeAccent, color: upgradeAccent }}
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {upgradePlan.positioning}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            ${upgradePlan.monthlyPrice}/month
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              or {upgradePlan.annualPriceLabel} billed annually
                            </Typography>
                          </Typography>
                        </Stack>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => navigate("/pricing")}
                          sx={{ whiteSpace: "nowrap", bgcolor: upgradeAccent, "&:hover": { bgcolor: upgradeAccent } }}
                        >
                          {upgradePlan.cta}
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        )}

        <Box>
          <Button variant="text" onClick={() => navigate("/my-leagues")}>
            Back to My Leagues
          </Button>
        </Box>
      </Stack>
    </Container>
  );
}
